import React, { useMemo, useState } from 'react';
import { Button, FormHelperText, Typography } from '@material-ui/core';

const stripDangerousNodes = (doc) => {
  doc
    .querySelectorAll('script, foreignObject')
    .forEach((node) => node.parentNode?.removeChild(node));

  doc.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attr) => {
      if (/^on/i.test(attr.name)) {
        element.removeAttribute(attr.name);
      }
    });
  });
};

export const sanitizeSvgMarkup = (value) => {
  if (!value) {
    return '';
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(value, 'image/svg+xml');
  const parserError = parsed.querySelector('parsererror');

  if (parserError) {
    throw new Error(parserError.textContent || 'Invalid SVG markup');
  }

  stripDangerousNodes(parsed);

  const svg = parsed.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
    throw new Error('Provide a valid <svg> root element.');
  }

  return svg.outerHTML;
};

const InlineSvgPreview = ({ value, compact = false, className }) => {
  const [showSource, setShowSource] = useState(false);

  const previewState = useMemo(() => {
    if (!value) {
      return { markup: '', error: null };
    }

    try {
      return { markup: sanitizeSvgMarkup(value), error: null };
    } catch (error) {
      return { markup: '', error: error.message || 'Unable to render SVG' };
    }
  }, [value]);

  if (compact) {
    if (!value) {
      return <span>—</span>;
    }

    if (!previewState.markup) {
      return <span title={previewState.error || 'Invalid SVG'}>⚠️</span>;
    }

    return (
      <span
        className={`inline-svg-preview inline-svg-preview--compact ${className || ''}`}
        style={{
          display: 'inline-flex',
          width: 48,
          height: 48,
          alignItems: 'center',
          justifyContent: 'center',
        }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: previewState.markup }}
      />
    );
  }

  return (
    <div className={`inline-svg-preview ${className || ''}`}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          SVG Preview
        </Typography>
        {value ? (
          <Button
            type="button"
            size="small"
            onClick={() => setShowSource((prev) => !prev)}
            color="primary"
          >
            {showSource ? 'Show Preview' : 'View Source'}
          </Button>
        ) : null}
      </div>
      {!value && <FormHelperText>Paste a valid SVG snippet to see a live preview.</FormHelperText>}
      {previewState.error && (
        <FormHelperText error data-testid="svg-preview-error">
          {previewState.error}
        </FormHelperText>
      )}
      {value && !previewState.error && !showSource && (
        <div
          style={{
            border: '1px solid rgba(0,0,0,0.1)',
            padding: 8,
            borderRadius: 4,
            minHeight: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: previewState.markup }}
        />
      )}
      {value && !previewState.error && showSource && (
        <pre
          style={{
            border: '1px solid rgba(0,0,0,0.1)',
            padding: 8,
            borderRadius: 4,
            maxHeight: 200,
            overflow: 'auto',
            backgroundColor: 'rgba(0,0,0,0.04)',
          }}
        >
          {previewState.markup}
        </pre>
      )}
      <FormHelperText>
        Scripts, <code>&lt;foreignObject&gt;</code>, and event handlers are stripped automatically
        for safety.
      </FormHelperText>
    </div>
  );
};

export default InlineSvgPreview;
